// Formatação compartilhada entre `DisbursementHeader` e `DisbursementList` — um único
// lugar para dinheiro, competência e a regra "é origem de IA?" evita que os dois
// componentes divirjam silenciosamente (ver review do Task 15).
import { formatNumberToCurrency } from "@utils/formatNumber";

const COMPETENCE = /^\d{4}-\d{2}$/;

/**
 * Dinheiro do módulo de desembolso, SEMPRE em R$.
 *
 * Duas decisões moram nesta função:
 *
 * 1. `checkCub = false`. As regras, os ajustes e a cobertura são centavos de REAL
 *    gravados como inteiro (`amountCents`, `coveredCents`, `plannedCents`); converter
 *    para CUB aqui dividiria um valor contratual por um indexador e mostraria um
 *    número que não existe em nenhum contrato. O gráfico de pré-visualização
 *    (`RulePreviewChart`) segue a mesma unidade, então a tela toda fala R$ mesmo numa
 *    obra em CUB. Desvio declarado no PR.
 * 2. `formatNumberToCurrency` é chamado INCONDICIONALMENTE. Ele lê
 *    `useProjectContext` por dentro — é um hook disfarçado — e um curto-circuito em
 *    `Number.isFinite` mudaria a CONTAGEM de hooks do componente hospedeiro assim que
 *    um valor virasse `null` (é exatamente o "change in the order of Hooks" do React).
 *    O `null`/`NaN` vira "—" DEPOIS da chamada, nunca no lugar dela.
 */
export const money = (cents) => {
  const finite = Number.isFinite(cents);
  const text = formatNumberToCurrency(finite ? cents / 100 : 0, false);
  return finite ? text : "—";
};

/**
 * `competence` esperado no formato `AAAA-MM`. Qualquer outra coisa (ausente,
 * malformada, de uma regra gravada antes de uma mudança de validação) vira "—"
 * em vez de quebrar a linha ou mostrar `undefined`.
 */
export const competenceText = (competence) => {
  if (!COMPETENCE.test(String(competence ?? ""))) return "—";
  return `${competence.slice(5, 7)}/${competence.slice(0, 4)}`;
};

/**
 * Único critério de "isso veio da IA" — usado tanto pela etiqueta de origem
 * quanto pelo filtro de origem, pra nunca divergir (`source` ausente/desconhecido
 * sempre conta como manual).
 */
export const isAiSource = (source) => (
  source === "ai_assistant" || source === "ai_suggestion" || source === "ai"
);

/** Percentual em PT-BR a partir de basis points (`9950` → `99,5%`). */
export const percentLabel = (bps) => {
  const value = Number.isFinite(bps) ? bps / 100 : 0;
  return `${value.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;
};

/**
 * Coerções dos campos numéricos do editor de regra. Vivem aqui porque o offcanvas
 * e os dois formulários que ele compõe (`RuleShortcutFields`, `RuleProfileTable`)
 * precisam da MESMA leitura — divergir aqui é gravar um perfil diferente do que
 * o cliente vê na tela.
 */
export const bpsToPercentInput = (bps) => (
  Number.isFinite(bps) ? String(Math.round(bps) / 100) : ""
);

/**
 * Percentual entra em % e vive em basis points: nunca `parseFloat` sobre centavos.
 * Campo vazio vira NaN de propósito — 0 bps é uma linha inválida com mensagem
 * própria, e silenciar o vazio como 0 esconderia que a linha está incompleta.
 */
export const percentInputToBps = (value) => {
  const text = String(value ?? "").trim().replace(",", ".");
  if (text === "") return NaN;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : NaN;
};

/** Inteiro truncado; vazio ou não numérico vira NaN (mesma razão do acima). */
export const integerInput = (value) => {
  const text = String(value ?? "").trim();
  if (text === "") return NaN;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : NaN;
};
